import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../utils/formatters';
import { isItemUnshippable } from './utils/checkoutUtils';
import MerchantItemsList from './components/MerchantItemsList';
import OrderTotalsDisplay from './components/OrderTotalsDisplay';

// Helper function to check if an item is unshippable
// const isItemUnshippable = (item, unshippableItems) => {
//   const itemId = item.id || item.cartItemIndex;
//   const method = item.deliveryMethod || 'shipping';
  
//   // Check in the new structure
//   if (typeof unshippableItems === 'object' && !Array.isArray(unshippableItems)) {
//     // If unshippableItems is the new object structure
//     if (unshippableItems[method]) {
//       // Check all address groups for this method
//       for (const addressId in unshippableItems[method]) {
//         if (unshippableItems[method][addressId]?.includes(itemId)) {
//           return true;
//         }
//       }
//     }
    
//     // Also check legacy array for backward compatibility
//     if (unshippableItems.legacy && Array.isArray(unshippableItems.legacy)) {
//       return unshippableItems.legacy.includes(itemId);
//     }
    
//     return false;
//   }
  
//   // Fallback to legacy behavior if unshippableItems is still an array
//   return Array.isArray(unshippableItems) && unshippableItems.includes(itemId);
// };

const OrderSummary = ({ cartItems }) => {
  // Get checkout state including the currency
  const { selectedShippingRate, deliveryGroups, checkoutCurrency } = useSelector(state => state.checkout);
  const { unshippableItems = [] } = useSelector(state => state.checkout);

  // Log the cart items and currency from props and redux state
  React.useEffect(() => {
    const itemCurrencies = cartItems.map(item => (item.currency || 'USD').toUpperCase());
    const uniqueCurrencies = [...new Set(itemCurrencies)];
    
    console.log('OrderSummary - Cart Items:', {
      count: cartItems.length,
      currencies: uniqueCurrencies,
      checkoutCurrency: checkoutCurrency,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        currency: item.currency || 'USD'
      }))
    });
    
    // Verify that all items have the same currency that matches checkoutCurrency
    const matchingCurrency = cartItems.every(item => 
      (item.currency || 'USD').toUpperCase() === (checkoutCurrency || 'USD').toUpperCase()
    );
    
    if (!matchingCurrency) {
      console.warn('OrderSummary - Warning: Not all cart items match the checkout currency!');
    }
  }, [cartItems, checkoutCurrency]);
  
  // Get all selected shipping rates from different address groups
  const allShippingRates = useMemo(() => {
    const rates = [];
    
    // Add main selected rate if exists
    if (selectedShippingRate) {
      rates.push(selectedShippingRate);
    }
    
    // Add rates from shipping address groups
    if (deliveryGroups?.shipping?.addressGroups) {
      Object.values(deliveryGroups.shipping.addressGroups).forEach(group => {
        if (group.rate && !rates.some(r => r.id === group.rate.id)) {
          rates.push(group.rate);
        }
      });
    }
    
    // Add rates from pickup locations if applicable
    if (deliveryGroups?.pickup?.locationGroups) {
      // Pickup might have associated costs in some cases
    }
    
    return rates;
  }, [selectedShippingRate, deliveryGroups]);
  
  // Calculate total shipping cost from all rates
  const totalShippingCost = useMemo(() => {
    return allShippingRates.reduce((sum, rate) => {
      return sum + (parseFloat(rate.price) || parseFloat(rate.amount) || 0);
    }, 0);
  }, [allShippingRates]);
  
  // Mark unshippable items without removing them
  const itemsWithShippingStatus = useMemo(() => {
    if (!cartItems) return [];
    
    return cartItems.map(item => {
      return {
        ...item,
        isUnshippable: isItemUnshippable(item, unshippableItems)
      };
    });
  }, [cartItems, unshippableItems]);
  
  // Filter shippable items for total calculations only
  // const shippableItems = useMemo(() => { // No longer needed here, handled by validItemsForTotal
  //   return itemsWithShippingStatus.filter(item => !item.isUnshippable);
  // }, [itemsWithShippingStatus]);
  
  // Group all items by merchant for display
  const groupedItems = useMemo(() => {
    if (!itemsWithShippingStatus) return {};
    
    return itemsWithShippingStatus.reduce((grouped, item) => {
      const merchantId = item.merchant?.id || 'unknown';
      const merchantName = item.merchant?.name || item.merchant?.username || 'Unknown Seller';
      
      if (!grouped[merchantId]) {
        grouped[merchantId] = {
          name: merchantName,
          items: []
        };
      }
      
      grouped[merchantId].items.push(item);
      
      return grouped;
    }, {});
  }, [itemsWithShippingStatus]);
  
  // Group items by delivery method
  const itemsByDeliveryMethod = useMemo(() => {
    return itemsWithShippingStatus.reduce((grouped, item) => {
      const method = item.deliveryMethod || 'shipping'; // Default to shipping
      
      if (!grouped[method]) {
        grouped[method] = [];
      }
      
      grouped[method].push(item);
      return grouped;
    }, {});
  }, [itemsWithShippingStatus]);
  
  // Get valid items for total calculation (non-unshippable shipping items + pickup + same-day)
  const validItemsForTotal = useMemo(() => {
    // Shipping items that aren't marked as unshippable
    const validShippingItems = (itemsByDeliveryMethod.shipping || [])
      .filter(item => !item.isUnshippable);
    
    // Pickup and same-day items (considered always valid)
    const pickupItems = itemsByDeliveryMethod.pickup || [];
    const sameDayItems = itemsByDeliveryMethod.sameDay || [];
    
    return [...validShippingItems, ...pickupItems, ...sameDayItems];
  }, [itemsByDeliveryMethod]);
  
  // Check if we have any valid items (shippable items or pickup/sameday)
  const hasValidItems = validItemsForTotal.length > 0;
  
  // Calculate subtotal, fees, tax, and total - only for valid items
  const orderTotals = useMemo(() => {
    let subtotal = 0;
    
    // Calculate subtotal from valid items
    validItemsForTotal.forEach(item => {
      if (item.type === 'buy') {
        subtotal += item.price * item.quantity;
      } else if (item.type === 'rent') {
        // For rental items, use the rental price
        if (item.rentalPeriod && item.rentalPeriod.price) {
          subtotal += item.rentalPeriod.price * item.quantity;
        } else if (item.rentTimeSelect && item.rentTimeSelect.price) {
          subtotal += item.rentTimeSelect.price * item.quantity;
        } else if (item.rentPriceAndTime && item.rentPriceAndTime.length > 0 && item.rentPriceAndTime[0].price) {
          subtotal += item.rentPriceAndTime[0].price * item.quantity;
        } else {
          subtotal += item.price * item.quantity; // Fallback for rent if specific rental price missing
        }
      } else {
        // Handle other types like 'swap' or default to item.price
        subtotal += item.price * item.quantity; 
      }
    });
    
    // Calculate platform fee, tax, and shipping
    const platformFee = subtotal * 0.1; // 10% platform fee
    const tax = (subtotal + platformFee) * 0.08; // 8% tax
    const shippingCost = totalShippingCost;
    
    // Calculate total
    const total = subtotal + platformFee + tax + shippingCost;
    
    return {
      subtotal,
      platformFee,
      tax,
      shippingCost,
      total
    };
  }, [validItemsForTotal, totalShippingCost]);
  
  // Calculate security deposit for rental items (only for valid items)
  const securityDeposit = useMemo(() => {
    return validItemsForTotal.reduce((acc, item) => {
      if (item.type === 'rent') {
        // Check for deposit in different property locations
        if (item.rentDeposit && item.rentDeposit > 0) {
          return acc + (parseInt(item.rentDeposit) * item.quantity);
        } else if (item.requireDeposit === true && item.rentDeposit) {
          return acc + (parseInt(item.rentDeposit) * item.quantity);
        } else if (item.deposit && item.deposit > 0) {
          return acc + (parseInt(item.deposit) * item.quantity);
        } else {
          // If no specific deposit is defined, use 50% of item price as placeholder
          return acc + (item.price * 0.5 * item.quantity);
        }
      }
      return acc;
    }, 0);
  }, [validItemsForTotal]);
  
  // Use the checkout currency from Redux store instead of trying to guess from items
  const primaryCurrency = checkoutCurrency || 'USD';
  
  // Display message if all items are unshippable
  if (!hasValidItems && itemsWithShippingStatus.length > 0) {
    return (
      <div>
        {/* Show warning about all items being unshippable */}
        <div className="bg-red-50 border border-red-200 p-4 rounded-md mb-4">
          <p className="text-red-700">
            None of your items can be shipped to the selected address. Please try a different shipping address.
          </p>
        </div>
        
        <MerchantItemsList groupedItems={groupedItems} />
        
        <OrderTotalsDisplay 
          orderTotals={orderTotals} 
          primaryCurrency={primaryCurrency} 
          totalShippingCost={totalShippingCost}
          allShippingRates={allShippingRates}
          securityDeposit={securityDeposit}
          hasValidItems={hasValidItems}
        />
      </div>
    );
  }

  // If no items at all
  if (itemsWithShippingStatus.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
        <p className="text-yellow-700">
          No items in your cart. Please add items to continue.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Add count of unshippable items if any */}
      {itemsWithShippingStatus.some(item => item.isUnshippable) && (
        <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md mb-4">
          <p className="text-yellow-700 text-sm">
            Some items cannot be shipped to the selected address and have been excluded from order total.
          </p>
        </div>
      )}
      
      <MerchantItemsList groupedItems={groupedItems} />
      
      <OrderTotalsDisplay 
        orderTotals={orderTotals} 
        primaryCurrency={primaryCurrency} 
        totalShippingCost={totalShippingCost}
        allShippingRates={allShippingRates}
        securityDeposit={securityDeposit}
        hasValidItems={hasValidItems}
      />
      
      {validItemsForTotal.some(item => item.type === 'rent') && hasValidItems && (
        <div className="mt-4 p-3 bg-blue-50 rounded-md text-xs text-gray-700">
          <p className="font-medium mb-1">Rental Information:</p>
          <p>Security deposits are fully refundable upon return of items in their original condition within the rental period.</p>
        </div>
      )}
    </div>
  );
};

export default OrderSummary; 